import React from "react";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import "../App.css";
import "../components/layout/privacy and policy/shipping.css";
import { Helmet } from "react-helmet-async";

const pageTitle = "Shipping & Delivery Policy | AIGIO";
const pageDescription = "Understand our shipping and delivery policies for AIGIO services.";

type Block = { type: "p"; text: string };

type Section = {
  id: string;
  title: string;
  number?: string;
  blocks: Block[];
};

const LAST_UPDATED = "01 July 2026";

const SECTIONS: Section[] = [
  {
    id: "digital-delivery",
    title: "Digital delivery",
    number: "1",
    blocks: [
      {
        type: "p",
        text: "All products and outputs - research briefs, brand identity, strategy, content, and website or social audits are delivered electronically and instantly inside your AIGIO account as soon as they are generated.",
      },
    ],
  },
  {
    id: "subscription-activation",
    title: "Subscription and credit activation",
    number: "2",
    blocks: [
      {
        type: "p",
        text: "When you subscribe to a plan, your access and the credits included with that plan are activated immediately after a successful payment is confirmed by the payment gateway. You can start using the Service right away.",
      },
    ],
  },
  {
    id: "delivery-issues",
    title: "If delivery does not happen",
    number: "3",
    blocks: [
      {
        type: "p",
        text: "If a payment succeeds but your subscription, credits, or an output are not delivered to your account due to a technical issue on our side, contact support@aigio.ai. We will deliver the service or refund you under our Refund & Cancellation Policy.",
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    number: "4",
    blocks: [
      {
        type: "p",
        text: "For any delivery-related questions, email support@aigio.ai.",
      },
    ],
  },
];

function renderBlock(block: Block, key: number) {
  return (
    <p key={key} className="legal-paragraph">
      {block.text}
    </p>
  );
}

const ShippingPolicy: React.FC = () => {
  const [activeId, setActiveId] = React.useState(SECTIONS[0].id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 }
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="page-shell page-shell--legal">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <Header />

        <main className="shipping-page">
          <div className="legal-page__inner">
            <p className="legal-page__kicker">LEGAL DOCUMENTATION</p>
            <h1 className="legal-page__title">Shipping & Delivery Policy</h1>
            <p className="legal-page__updated">
              Last updated: {LAST_UPDATED}. AIGIO is a product of Orglife Private Limited
            </p>

            <div className="legal-page__body">
              <aside className="legal-toc" aria-label="Table of contents">
                <ul className="legal-toc__list">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`legal-toc__link ${
                          activeId === s.id ? "legal-toc__link--active" : ""
                        }`}
                      >
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="legal-content">
                {/* Introductory section matching the document header perfectly */}
                <div style={{ marginBottom: "32px" }}>
                  <p className="legal-paragraph">
                    AIGIO, a product of Orglife Private Limited, provides digital services only. This policy explains how "delivery" works for a software product.
                  </p>
                  <p className="legal-paragraph">
                    No physical shipping. AIGIO does not sell or ship any physical goods. There are no shipping charges, courier partners, or delivery addresses involved.
                  </p>
                </div>

                {/* Numbered Sections */}
                {SECTIONS.map((s) => (
                  <section key={s.id} id={s.id} className="legal-section">
                    <h2 className="legal-section__title">
                      {s.number ? `${s.number}. ` : ""}{s.title}
                    </h2>
                    {s.blocks.map((b, j) => renderBlock(b, j))}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ShippingPolicy;